import { FC, useEffect, useState } from 'react';
import { captureException } from '@sentry/react';
import { Checkbox, Input, Tag } from 'antd';
import { AxiosResponse } from 'axios';
import { TbSearch } from 'react-icons/tb';

import { authorizedAxiosInstance } from '@/clients';
import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useEntitlements } from '@/hooks/useEntitlements';
import { PlanTag } from '@/shared';
import { BaseSkeleton } from '@/shared/components/skeletons';

interface ExchangesProps {
    filters: any;
    handleChange: any;
}

export type FMPExchange = {
    exchange: string;
    name: string;
    countryName: string;
    countryCode: string;
    symbolSuffix: string;
    delay: string;
};

const Exchanges: FC<ExchangesProps> = ({ filters, handleChange }): JSX.Element => {
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(false);
    }, []);

    const [exchanges, setExchanges] = useState<FMPExchange[]>([]);

    const handleGetExchanges = async () => {
        try {
            setLoading(true);
            await authorizedAxiosInstance.get(`/api/vendors/fmp/exchanges`).then((res: AxiosResponse) => {
                if (res?.status === 200) {
                    setExchanges(res?.data);

                    setLoading(false);
                }
            });
        } catch (error) {
            setLoading(false);

            captureException(error);
        }
    };

    const { hasMinimumPlan } = useEntitlements();

    useEffect(() => {
        if (hasMinimumPlan('plus')) {
            handleGetExchanges();
        }
    }, []);

    const [searchText, setSearchText] = useState('');

    // Filter by key or value
    const filteredExchanges =
        exchanges?.length !== 0
            ? exchanges?.filter((key: FMPExchange) => key?.exchange?.toLowerCase().includes(searchText?.toLowerCase()))
            : [];

    const { token } = useAntDesignTheme();

    return !hasMinimumPlan('plus') ? (
        <div style={{ textAlign: 'center', margin: '2rem' }}>
            <PlanTag plan={'plus'} style={{ marginLeft: '0.5rem' }} />
        </div>
    ) : (
        <>
            <Input
                disabled={loading}
                placeholder='Search...'
                suffix={<TbSearch style={{ color: token.colorTextQuaternary }} />}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
            />
            {filteredExchanges?.length !== 0 && !loading ? (
                <>
                    <div style={{ marginBottom: '0.5rem' }}>
                        <small style={{ color: token.colorTextTertiary }}>{filteredExchanges?.length} Results</small>
                    </div>
                    <Checkbox.Group
                        value={filters?.exchanges}
                        onChange={(checkedValues) => {
                            handleChange(['exchanges'], checkedValues);
                        }}
                    >
                        {filteredExchanges?.map((ex: FMPExchange) => {
                            return (
                                <div key={ex?.exchange}>
                                    <Checkbox
                                        style={{ marginBottom: '0.125rem' }}
                                        value={ex?.exchange}
                                        checked={filters?.exchanges?.includes(ex)}
                                    />
                                    <span style={{ marginLeft: '0.5rem' }}>
                                        {ex?.name}
                                        <Tag
                                            style={{
                                                marginLeft: '0.5rem',

                                                borderColor: 'transparent',
                                            }}
                                        >
                                            <small>{ex?.exchange}</small>
                                        </Tag>
                                    </span>
                                </div>
                            );
                        })}
                    </Checkbox.Group>
                </>
            ) : (
                loading && (
                    <>
                        <BaseSkeleton title active round paragraph={{ rows: 14 }} style={{ marginTop: '1rem' }} />
                    </>
                )
            )}
        </>
    );
};

export default Exchanges;
